/**
 * Foundry → Requests (D441): people asking the organisation to approve a project, a deployment or a cluster. Those waiting
 * come first with Approve and Decline; the decided ones sit below, quieter. Approving adds the project to a connection
 * for the same directory or cluster (one you pick, or the one that matches), or starts a connection of its own.
 */

import type { FoundryConnection, FoundryConnectionRequest } from '@aioe/shared';
import { CircleQuestion, CircleCheck, Cloud } from '@gravity-ui/icons';
import { useState } from 'react';

import { foundryRoutes, problemText } from '../../api/client.ts';
import { timeAgo, useLoad } from '../../hooks.ts';
import { Button, ChoiceSelect, Empty, ErrorNote, inputClass, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark } from '../ui.tsx';
import { FormField, FormSheet } from '../ui/FormSheet.tsx';
import { hostOf, KIND_WORDS, requestTarget, requestUses, usePersonName } from './foundryWords.ts';
import { TONE } from './tone.ts';

const AUTO = '__auto';

/** Who asked, as a name; the request's own name for them, else the directory's, else a plain word. */
function Who({ q }: { q: FoundryConnectionRequest }) {
  const name = usePersonName(q.askedByName ? undefined : q.askedBy);
  return <>{q.askedByName ?? name ?? 'Someone'}</>;
}

/** The connections a request may be added to: the same kind, and the same directory or cluster. */
function compatible(q: FoundryConnectionRequest, connections: FoundryConnection[]): FoundryConnection[] {
  return connections.filter((c) => {
    if (c.kind !== q.kind) return false;
    if (q.kind === 'azure') return (c.tenantId ?? '').toLowerCase() === (q.tenantId ?? '').toLowerCase();
    return c.azureLocal?.cluster === q.azureLocal?.cluster && hostOf(c.azureLocal?.endpoint ?? '') === hostOf(q.azureLocal?.endpoint ?? '');
  });
}

function RequestDescription({ q }: { q: FoundryConnectionRequest }) {
  const uses = requestUses(q);
  return (
    <>
      <span className="block">
        <Who q={q} /> · {KIND_WORDS[q.kind]} · {hostOf(q.endpoint)}
      </span>
      {uses.length ? <span className="block truncate">{q.kind === 'azure' ? 'Deployments' : 'Models'}: {uses.join(', ')}</span> : null}
      {q.reason ? <span className="block">“{q.reason}”</span> : null}
      <span className="block tabular-nums">Asked {timeAgo(q.createdAt)}</span>
    </>
  );
}

export function RequestsPanel({ mayEdit }: { mayEdit: boolean }) {
  const list = useLoad(() => foundryRoutes.requests(), [], 30_000);
  const connections = useLoad(() => foundryRoutes.connections().catch(() => undefined), [], 60_000);
  const [answering, setAnswering] = useState<{ q: FoundryConnectionRequest; decision: 'approve' | 'decline' }>();
  const all = list.data?.requests;
  const pending = (all ?? []).filter((q) => q.state === 'pending').sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const decided = (all ?? []).filter((q) => q.state !== 'pending').sort((a, b) => (b.decidedAt ?? b.createdAt).localeCompare(a.decidedAt ?? a.createdAt));
  const approved = decided.filter((q) => q.state === 'approved').length;

  return (
    <PageColumn width="data">
      <PageHeader tone={TONE} icon={<CircleQuestion />} title="Foundry requests" subtitle="What people have asked to use that the organisation has not approved yet." />
      <ErrorNote error={list.error} />
      <div className="art-stats">
        <Stat tone={TONE} icon={<CircleQuestion />} label="Waiting for you" value={all ? pending.length : undefined} detail={pending.length ? `Oldest ${timeAgo(pending[0]!.createdAt)}` : 'Nothing waiting'} needsYou={pending.length > 0} />
        <Stat tone={TONE} icon={<CircleCheck />} label="Approved" value={all ? approved : undefined} detail="Added to a connection" />
        <Stat tone={TONE} icon={<Cloud />} label="Declined" value={all ? decided.length - approved : undefined} detail="The person was told" />
      </div>

      <ListGroup
        title="Waiting for a decision"
        count={all ? pending.length : undefined}
        description="Approving adds the project to a connection, so the person and everyone else can use it. Declining tells the person, with your note."
        empty={all && pending.length === 0 ? <Empty tone={TONE} icon={<CircleQuestion />} title="Nothing waiting" body="When someone asks to use a Foundry project or cluster the organisation has not approved, the request lands here." /> : undefined}
      >
        {!all && !list.error ? <ListRow title="Reading the requests…" /> : null}
        {pending.map((q) => (
          <ListRow
            key={q.requestId}
            icon={<Cloud />}
            title={requestTarget(q)}
            description={<RequestDescription q={q} />}
            control={
              mayEdit ? (
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="primary" onPress={() => setAnswering({ q, decision: 'approve' })}>
                    Approve
                  </Button>
                  <Button size="sm" variant="secondary" onPress={() => setAnswering({ q, decision: 'decline' })}>
                    Decline
                  </Button>
                </div>
              ) : undefined
            }
          />
        ))}
      </ListGroup>

      {decided.length ? (
        <ListGroup title="Decided" count={decided.length}>
          {decided.map((q) => (
            <ListRow
              key={q.requestId}
              icon={<Cloud />}
              title={requestTarget(q)}
              description={
                <>
                  <RequestDescription q={q} />
                  {q.note ? <span className="block">Your note: {q.note}</span> : null}
                </>
              }
              status={<StatusMark state={q.state === 'approved' ? 'connected' : 'disabled'} label={q.state === 'approved' ? 'Approved' : 'Declined'} />}
            />
          ))}
        </ListGroup>
      ) : null}

      {answering ? (
        <DecisionSheet
          {...answering}
          connections={compatible(answering.q, connections.data?.connections ?? [])}
          onClose={() => setAnswering(undefined)}
          onDone={() => {
            setAnswering(undefined);
            list.reload();
            connections.reload();
          }}
        />
      ) : null}
    </PageColumn>
  );
}

/** The small sheet for one decision: where an approved project goes, or an optional note for a decline. */
function DecisionSheet({ q, decision, connections, onClose, onDone }: { q: FoundryConnectionRequest; decision: 'approve' | 'decline'; connections: FoundryConnection[]; onClose: () => void; onDone: () => void }) {
  const [connectionId, setConnectionId] = useState(AUTO);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const approving = decision === 'approve';
  const options: Record<string, string> = { [AUTO]: connections.length ? 'Let the platform choose' : 'Start a connection for it', ...Object.fromEntries(connections.map((c) => [c.id, c.name])) };
  const send = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await foundryRoutes.decideRequest(q.requestId, { decision, ...(note.trim() ? { note: note.trim() } : {}), ...(approving && connectionId !== AUTO ? { connectionId } : {}) });
      onDone();
    } catch (e) {
      setError(problemText(e));
      setBusy(false);
    }
  };
  return (
    <FormSheet
      width="md"
      title={`${approving ? 'Approve' : 'Decline'} ${requestTarget(q)}`}
      description={approving ? 'The project is added to the connection you pick, with the deployments and tools asked for, and the organisation’s Foundry policy is updated.' : 'The person is told it was declined, with your note if you add one.'}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <Button variant="tertiary" onPress={onClose} isDisabled={busy}>
            Cancel
          </Button>
          <Button variant={approving ? 'primary' : 'danger-soft'} onPress={() => void send()} isPending={busy}>
            {approving ? 'Approve' : 'Decline'}
          </Button>
        </>
      }
    >
      {approving ? (
        <FormField label="Add it to" hint={connections.length ? `Only connections for the same ${q.kind === 'azure' ? 'directory' : 'cluster'} are offered.` : `No connection yet covers this ${q.kind === 'azure' ? 'directory' : 'cluster'}, so one is started for it. You can rename it under Connections.`}>
          <ChoiceSelect label="Add it to" className="w-full" value={connectionId} options={options} onChange={setConnectionId} />
        </FormField>
      ) : null}
      <FormField label={approving ? 'A note for the person (optional)' : 'Why (optional)'}>
        <textarea className={`${inputClass} resize-y`} rows={3} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
      </FormField>
      <ErrorNote error={error} />
    </FormSheet>
  );
}
